import clsx from 'clsx';
import { Ref, forwardRef, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ProductWidget, isBeautyMatchingCategory } from '@vestico/model';

import { Button, UserContext } from '@vestico/shared';
import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import { userDataCompletedForAnyProduct } from '../model/helpers';
import { MatchedReviewContent, MatchedReviewContentProps } from './ReviewContentMatched';
import style from './ReviewContentText.module.css';

export interface TextReviewContentProps extends MatchedReviewContentProps {
  showBeginUserMatchInfoSection?: boolean;
  anyResponseHasMatch?: boolean;
  onBeginUserMatchInfo?: () => void;
}

export const TextReviewContent = forwardRef(
  (
    {
      response,
      products,
      showBeginUserMatchInfoSection,
      anyResponseHasMatch,
      onBeginUserMatchInfo,
      showProducts,
      onProductLinkClick,
    }: TextReviewContentProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const { hasMatchInfo } = response;
    const { reloadUser, user } = useContext(UserContext);

    const { t } = useTranslation();

    const handleClick = useCallback(() => {
      if (onBeginUserMatchInfo) {
        onBeginUserMatchInfo();
      }
    }, [onBeginUserMatchInfo]);

    const handleReset = useCallback(() => {
      if (reloadUser) {
        reloadUser(true);
      }
    }, [reloadUser]);

    const infoText = useMemo(() => {
      if (products) {
        for (const product of products) {
          if (isBeautyMatchingCategory(product.matchingCategory)) {
            return t('LOC_REVIEW_CONTENT_ENTER_BODY_INFO_TEXT_BEAUTY');
          }
        }
      }

      return t('LOC_REVIEW_CONTENT_ENTER_BODY_INFO_TEXT');
    }, [products, t]);

    const buttonText = useMemo(() => {
      if (products) {
        for (const product of products) {
          if (isBeautyMatchingCategory(product.matchingCategory)) {
            return t('LOC_REVIEW_CONTENT_ENTER_SKIN_INFO_BUTTON');
          }
        }
      }
      return t('LOC_REVIEW_CONTENT_ENTER_BODY_INFO_BUTTON');
    }, [products, t]);

    const [cachedProducts, setCachedProducts] = useState<ProductWidget[] | null | undefined>(undefined);

    useLayoutEffect(() => {
      if (products !== undefined) {
        setCachedProducts(products);
      }
    }, [products]);

    const enterMatchInfoPlaceholder = showBeginUserMatchInfoSection ? (
      // Ensure it always reserves some space
      <div className={clsx(style.enterMatchInfo)}>
        {/* Always insert the div if we're showing match info section */}
        {cachedProducts ? (
          <>
            <div className={clsx(globalStyle.textSmall, globalStyle.textBold, globalStyle.textColorSecondary)}>{infoText}</div>
            <div>
              <Button content={buttonText} onClick={handleClick} />
            </div>
          </>
        ) : null}
      </div>
    ) : null;

    const resetUserMatchInfoPlaceholder =
      !showBeginUserMatchInfoSection && products && hasMatchInfo && !anyResponseHasMatch && user && userDataCompletedForAnyProduct(products, user) ? (
        <div className={clsx(style.enterMatchInfo)}>
          <div>
            <div className={clsx(style.infoText)}>{t('LOC_REVIEW_CONTENT_NO_MATCH_AVAILABLE_TITLE')}</div>
            <div className={clsx(style.infoText)}>
              <Trans t={t} i18nKey="LOC_REVIEW_CONTENT_NO_MATCH_AVAILABLE_RESET" values={{ startOverButton: t('LOC_RESET_BODY_INFO_BUTTON') }}>
                <Button content={t('LOC_RESET_BODY_INFO_BUTTON')} plain size="small" onClick={handleReset} cssProperties={{ fontWeight: 600 }} />
              </Trans>
            </div>
          </div>
        </div>
      ) : null;

    return (
      <div className={clsx(style.root)} ref={ref}>
        {enterMatchInfoPlaceholder}
        {resetUserMatchInfoPlaceholder}
        <MatchedReviewContent response={response} products={products} showProducts={showProducts} onProductLinkClick={onProductLinkClick} />
      </div>
    );
  }
);
